/**
 * FreezeSentinel — the ONE always-on (NOT netobs-gated) client diagnostic.
 *
 * A bare requestAnimationFrame watchdog with two detectors, each unambiguous
 * about its side (see FreezeEventTypes):
 *
 *   1. CLIENT FREEZE — an rAF gap > CLIENT_FREEZE_THRESHOLD_MS: client frame
 *      production stopped. A longtask may prove a main-thread block; without
 *      one, renderer/compositor/GPU/host scheduling remains possible. Caught
 *      when the loop resumes.
 *   2. SERVER SILENCE — rAF is healthy but no server state-patch has arrived for
 *      > SERVER_SILENCE_THRESHOLD_MS: the server stalled or the transport
 *      hiccuped (remote entities frozen while the UI stays responsive).
 *
 * It is the deliberate, documented exception to `isNetobsEnabled()` because
 * catching UNPREDICTABLE freezes during NORMAL play (what DJ asked for) is
 * impossible if detection is gated to the `/debug` surface. The per-frame cost
 * is one `performance.now()` subtraction; it sends ONLY when something trips.
 *
 * On a trip it (1) sends `netobs:freezeEvent` → the server records a `system`
 * DomainLog event (free JSONL + history + attribute_at), and (2) emits
 * `EVENTS.SYSTEM_FREEZE` for the always-on in-game corner tag.
 *
 * NOT every trip is a freeze. Chrome throttles rAF for an unfocused window, and
 * that gap trips detector 1 exactly like a real stall does — the sentinel has no
 * way to not see it. It is classified, not suppressed: the server records those
 * under `raf_throttled` instead of `freeze_detected`, so every freeze query is
 * right by default while the session still knows when nobody was looking. The
 * rule is `isThrottledFrameGap()` in FreezeEventTypes; the wire is identical
 * either way, which is what keeps the `.strict()` schema out of the blast radius.
 *
 * Heartbeat: subscribes to `room.onStateChange` (every server state-patch) and
 * stamps the arrival; the silence detector measures `now - lastServerActivityMs`.
 * Tab-backgrounding pauses rAF (would look like a giant freeze) — guarded via
 * visibilitychange + a one-frame skip.
 */

import React, { useEffect } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import {
  FREEZE_CONFIG,
  classifyFocus,
  type FreezeAttribution,
  type FreezeEventWire,
  type FreezeKind,
} from '../../../shared/diagnostics/FreezeEventTypes';
import { FreezeAttributor } from '../../diagnostics/freezeAttribution';
import { createFrameBudgetMonitor } from '../../diagnostics/frameBudgetReporting';
import { AllocCounters } from '../../diagnostics/AllocCounters';
import { AtmosphereLayer } from '../../effects/atmosphere/AtmosphereLayer';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { PixiParticleEmitter } from '../../rendering/PixiParticleEmitter';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import {
  ServerActivityMonitor,
  type ServerActivityRoom,
} from './ServerActivityMonitor';
import {
  FreezeEventDeliveryQueue,
  type FreezeDeliverySnapshot,
  type FreezeEventTransport,
} from './FreezeEventDeliveryQueue';
import { bootPathname } from '../../../config/boot/bootUrl';

const freezeDeliveryQueue = new FreezeEventDeliveryQueue();
let freezeEventSequence = 0;

export function getFreezeDeliverySnapshot(): FreezeDeliverySnapshot {
  return freezeDeliveryQueue.getSnapshot();
}

function nextFreezeClientEventId(capturedAt: number): string {
  freezeEventSequence += 1;
  const randomId = globalThis.crypto?.randomUUID?.();
  return randomId
    ? `freeze-${randomId}`
    : `freeze-${capturedAt}-${freezeEventSequence}`;
}

function asFreezeTransport(room: ServerActivityRoom | null): FreezeEventTransport | null {
  const candidate = room as (ServerActivityRoom & Partial<FreezeEventTransport>) | null;
  return candidate && typeof candidate.send === 'function'
    ? candidate as ServerActivityRoom & FreezeEventTransport
    : null;
}

function readHeapMB(): number | null {
  const perf = (globalThis as { performance?: Performance & { memory?: { usedJSHeapSize: number } } }).performance;
  const used = perf?.memory?.usedJSHeapSize;
  return typeof used === 'number' && Number.isFinite(used) ? Math.round(used / 1048576) : null;
}

export const FreezeSentinel: React.FC = () => {
  useEffect(() => {
    let rafId = 0;
    let lastFrameAt = performance.now();
    let lastClientLogAt = 0;
    // Skip the first gap after mount AND after any tab-unhide (rAF was paused).
    let skipNextGap = true;

    const serverActivity = new ServerActivityMonitor(
      {
        monotonicNow: () => performance.now(),
        wallNow: () => Date.now(),
      },
      (snapshot) => {
        try {
          const tracer = (globalThis as {
            nm?: { tracer?: { recordServerActivityDiagnostics?: (value: typeof snapshot) => void } };
          }).nm?.tracer;
          tracer?.recordServerActivityDiagnostics?.(snapshot);
        } catch {
          // Existing diagnostics pipeline is best-effort and never gameplay-affecting.
        }
      },
    );

    // Always-on cause collector: samples AllocCounters (~1Hz) + longtasks + heap
    // so a client_freeze can explain itself (top hot-path movers / GC signal).
    const attributor = new FreezeAttributor();
    attributor.start();
    // Sustained slowness (no single 150 ms gap) — judged per 10 s window and
    // recorded as `frame_budget_degraded` (plan 13 §8, FrameBudgetMonitor).
    const frameBudget = createFrameBudgetMonitor();
    frameBudget.start();

    const onVisibility = (): void => {
      if (document.hidden) return;
      lastFrameAt = performance.now();
      serverActivity.resetSilenceClock();
      skipNextGap = true;
    };
    document.addEventListener('visibilitychange', onVisibility);

    // (Re)bind the server-activity heartbeat to the current room. The monitor
    // owns listener cleanup and ignores late callbacks from a replaced room.
    const ensureHeartbeat = (): void => {
      const room = (globalThis as { nm?: { getRoom?: () => ServerActivityRoom | null } })
        .nm?.getRoom?.() ?? null;
      serverActivity.bindRoom(room);
      freezeDeliveryQueue.flush(asFreezeTransport(room));
    };

    const collectFreezeContext = (): Pick<FreezeEventWire, 'levelId' | 'atmosphere' | 'renderer' | 'runtime' | 'sceneTickMs'> => {
      // Level id: prefer MapSystem, fall back to AtmosphereLayer mount state.
      const mapSys = ServiceRegistry.getInstance().get('MapSystem', { optional: true }) as
        | { getCurrentLevel(): { id: string } | null }
        | undefined;
      const mapLevel = mapSys?.getCurrentLevel?.()?.id;
      const atmoSnap = AtmosphereLayer.getDiagnosticsSnapshot();
      const levelId = mapLevel ?? atmoSnap.levelId ?? undefined;

      // Renderer / ticker / particle state.
      const rendererStats = PixiOverlayHost.getRendererStats();
      const renderer = rendererStats
        ? {
            ...rendererStats,
            activeParticleCount: PixiParticleEmitter.getTotalParticles(),
            activeEmitterCount: PixiParticleEmitter.getActiveEmitterCount(),
          }
        : undefined;

      // Scene update time (GameHost rAF loop) — helps tell whether the stall
      // is in the substrate-agnostic scene tick or somewhere else.
      const gameHost = (globalThis as unknown as { __GAME_HOST__?: { lastTickMs?: number } }).__GAME_HOST__;
      const sceneTickMs = typeof gameHost?.lastTickMs === 'number' ? gameHost.lastTickMs : undefined;

      // Selected cumulative counters that mirror the attribution hot-paths.
      const all = AllocCounters.getStats().counters;
      const runtime: Record<string, number> = {};
      const keys = [
        'pixi.ticker.frame',
        'pixi.ticker.listenersFired',
        'tween.step',
        'emitter.tick.entry',
        'emitter.tick.particlesIterated',
        'spatial.queryRangeInto',
      ];
      for (const k of keys) {
        const v = all[k];
        if (typeof v === 'number') runtime[k] = v;
      }

      return {
        ...(levelId ? { levelId } : {}),
        atmosphere: {
          presetId: atmoSnap.presetId,
          layerKinds: atmoSnap.layerKinds,
          spriteCount: atmoSnap.spriteCount,
          lightCount: atmoSnap.lightCount,
        },
        ...(renderer ? { renderer } : {}),
        ...(sceneTickMs !== undefined ? { sceneTickMs } : {}),
        runtime,
      };
    };

    const emitFreeze = (kind: FreezeKind, stallMs: number, attribution?: FreezeAttribution): void => {
      const activitySnapshot = kind === 'server_silence' ? serverActivity.getSnapshot() : null;
      const recentHeartbeats = activitySnapshot?.recentHeartbeats ?? [];
      const lastHeartbeat = recentHeartbeats.length > 0
        ? recentHeartbeats[recentHeartbeats.length - 1]
        : null;
      const capturedAt = Date.now();
      const context = collectFreezeContext();
      const freezeAttribution = attribution ?? (kind === 'client_freeze'
        ? attributor.snapshot(stallMs, performance.now(), {
            lastTickerMs: context.renderer?.lastTickerMs,
            sceneTickMs: context.sceneTickMs,
            // Splits benign browser throttling of an occluded window from a
            // focused window whose frames stopped reaching the screen.
            focused: typeof document !== 'undefined'
              ? !document.hidden && document.hasFocus()
              : undefined,
          })
        : undefined);
      const evt: FreezeEventWire = {
        clientEventId: nextFreezeClientEventId(capturedAt),
        capturedAt,
        kind,
        stallMs: Math.round(stallMs),
        route: bootPathname(),
        heapUsedMB: readHeapMB(),
        ...context,
        documentHidden: typeof document !== 'undefined' ? document.hidden : undefined,
        visibilityState: typeof document !== 'undefined'
          ? (document.visibilityState as FreezeEventWire['visibilityState'])
          : undefined,
        hasFocus: typeof document !== 'undefined' ? document.hasFocus() : undefined,
        focusClass: classifyFocus({
          documentHidden: typeof document !== 'undefined' ? document.hidden : undefined,
          hasFocus: typeof document !== 'undefined' ? document.hasFocus() : undefined,
        }),
        ...(freezeAttribution ? { attribution: freezeAttribution } : {}),
        ...(activitySnapshot ? {
          serverActivity: {
            bindingGeneration: activitySnapshot.bindingGeneration,
            lastSource: activitySnapshot.lastSource,
            heartbeatReceipts: activitySnapshot.heartbeatReceipts,
            statePatchReceipts: activitySnapshot.statePatchReceipts,
            lastHeartbeatSequence: lastHeartbeat?.sequence ?? null,
            lastServerSequence: lastHeartbeat?.serverSequence ?? null,
            lastHeartbeatTraceId: lastHeartbeat?.traceId ?? null,
            lastRoomInstanceId: lastHeartbeat?.roomInstanceId ?? null,
            lastHeartbeatServerSentAt: lastHeartbeat?.serverSentAt ?? null,
            lastHeartbeatClientReceivedAt: lastHeartbeat?.clientReceivedAt ?? null,
            lastRawFrameSequence: lastHeartbeat?.rawFrameSequence ?? null,
            lastRawFrameReceivedAt: lastHeartbeat?.rawFrameReceivedAt ?? null,
            lastRawToCallbackMs: lastHeartbeat?.rawToCallbackMs ?? null,
            lastHeartbeatGapMs: lastHeartbeat?.gapMs ?? null,
          },
        } : {}),
      };
      // (1) persist server-side as a `system` DomainLog event. Diagnostic-only:
      // a failed send must never perturb gameplay.
      try {
        const room = (globalThis as { nm?: { getRoom?: () => ServerActivityRoom | null } })
          .nm?.getRoom?.() ?? null;
        freezeDeliveryQueue.submit(evt, asFreezeTransport(room));
      } catch {
        // swallow — never gameplay-affecting.
      }
      // (2) in-game corner tag.
      try {
        EventBus.getInstance().emit(EVENTS.SYSTEM_FREEZE, evt);
      } catch {
        // swallow.
      }
    };

    const loop = (): void => {
      const now = performance.now();
      const gap = now - lastFrameAt;
      lastFrameAt = now;
      ensureHeartbeat();
      // Refresh the rolling counter window (~1Hz, throttled internally) so a
      // freeze always has a recent prev→curr diff to attribute against.
      if (attributor.shouldSample(now)) attributor.sampleCounters(now);

      if (skipNextGap) {
        skipNextGap = false;
      } else if (document.hidden) {
        // tab hidden — rAF throttled/paused; ignore.
      } else if (gap > FREEZE_CONFIG.CLIENT_FREEZE_THRESHOLD_MS) {
        // CLIENT render-loop freeze (rAF stalled). Gaps above the ceiling are
        // debugger-pause / OS-sleep / hidden-throttle artifacts, not felt freezes
        // — drop them. Either way the heartbeat couldn't update during the gap, so
        // reset it to avoid a spurious silence on the next frames, and re-arm.
        if (gap < FREEZE_CONFIG.DEBUGGER_PAUSE_CEILING_MS
            && now - lastClientLogAt >= FREEZE_CONFIG.MIN_LOG_INTERVAL_MS) {
          lastClientLogAt = now;
          // Attribute the freeze to its cause (top hot-path movers / longtask /
          // GC heap-drop / rAF throttling) so it explains itself in the domain log + attribute_at.
          emitFreeze('client_freeze', gap);
        }
        serverActivity.resetSilenceClock();
      } else {
        // rAF healthy → watch for SERVER silence (remote entities frozen but UI live).
        const silence = serverActivity.checkSilence(FREEZE_CONFIG.SERVER_SILENCE_THRESHOLD_MS);
        if (silence) emitFreeze('server_silence', silence.stallMs);
      }
      // Record this (healthy) frame's heap so the NEXT freeze can report the
      // delta across its gap (a sharp drop ⇒ GC reclaimed mid-stall).
      attributor.noteFrameHeap();
      frameBudget.noteFrame(now, !document.hidden && document.hasFocus());
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('visibilitychange', onVisibility);
      serverActivity.dispose();
      attributor.stop();
      frameBudget.stop();
    };
  }, []);

  return null;
};
